import React, { FC, ReactElement } from 'react';

import styled from 'styled-components';

import aampeTheme from '../../../constants/aampeTheme';

interface ComposerNxChannelSelectionProps {
    icon: ReactElement;
    channelName: string;
    onExistingClick: () => void;
    onComposerNXClick: () => void;
}

const ComposerNxChannelSelection: FC<ComposerNxChannelSelectionProps> = ({
    icon,
    channelName,
    onExistingClick,
    onComposerNXClick,
}) => {
    return (
        <Container>
            <Header>
                <IconWrapper>{icon}</IconWrapper>
                <ChannelName>{channelName}</ChannelName>
            </Header>

            <ButtonsContainer>
                <OutlinedButton
                    onClick={onExistingClick}
                    aria-label="Create mobile push message with existing composer"
                >
                    Proceed with existing experience
                </OutlinedButton>

                <GradientButton
                    onClick={onComposerNXClick}
                    aria-label="Try the new Composer NX experience for mobile push"
                >
                    Try Aampe Composer NX
                </GradientButton>
            </ButtonsContainer>
        </Container>
    );
};

const Container = styled.div(() => ({
    backgroundColor: aampeTheme.colors.white,
    border: `1px solid ${aampeTheme.colors.bordersWG01}`,
    borderRadius: aampeTheme.borderRadius.medium,
    padding: aampeTheme.spacing4(5),
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(6),
}));

const Header = styled.div(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(2),
}));

const IconWrapper = styled.div(() => ({
    width: aampeTheme.size.icon.default,
    height: aampeTheme.size.icon.default,
    aspectRatio: '1/1',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
}));

const ChannelName = styled.span(() => ({
    color: aampeTheme.colors.textAccessibleWG01,
    fontSize: aampeTheme.size.font.default,
    fontWeight: aampeTheme.weights.medium,
}));

const ButtonsContainer = styled.div(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(2),
}));

const baseButtonStyles = {
    display: 'flex',
    padding: aampeTheme.spacing4(2.75, 3.25),
    justifyContent: 'center',
    alignItems: 'center',
    gap: aampeTheme.spacing4(1),
    alignSelf: 'stretch',
    borderRadius: aampeTheme.borderRadius.medium,
    fontSize: aampeTheme.size.font.textMedium,
    fontWeight: aampeTheme.weights.medium,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
};

const OutlinedButton = styled.button(() => ({
    ...baseButtonStyles,
    border: `1px solid ${aampeTheme.colors.solidA01}`,
    backgroundColor: aampeTheme.colors.white,
    color: aampeTheme.colors.solidA01,
    '&:hover': {
        backgroundColor: aampeTheme.colors.backgroundWG01,
    },
    '&:focus-visible': {
        outline: `2px solid ${aampeTheme.colors.solidA01}`,
    },
}));

const GradientButton = styled.button(() => ({
    ...baseButtonStyles,
    border: 'none',
    background: 'linear-gradient(90deg, #E05D12 0%, #BC4F0B 100%)',
    color: aampeTheme.colors.white,
    '&:hover': {
        filter: 'brightness(1.1)',
    },
    '&:focus-visible': {
        outline: `2px solid ${aampeTheme.colors.solidA01}`,
    },
}));

export default ComposerNxChannelSelection;
